"use client";

import { useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { useAuth } from "@/hooks/useAuth";
import { useModal } from "@/contexts/modal-context";
import { useActivateReferral } from "@/hooks/use-activate-referral";
import { ReferralModal } from "@/components/referral-modal";
import { CAMPAIGN_CONFIG } from "@/config/campaign";
import { STORAGE_KEYS } from "@/config/constants";
import { usePlausible } from "next-plausible";

export const ReferralWrapper = () => {
  const { address, isAuthenticated } = useAuth();
  const searchParams = useSearchParams();
  const { isReferralModalVisible, showReferralModal, hideReferralModal } = useModal();
  const plausible = usePlausible();

  const activateReferralMutation = useActivateReferral(
    CAMPAIGN_CONFIG.id,
    CAMPAIGN_CONFIG.groupId,
    address,
    isAuthenticated
  );

  // Get referral code from URL or localStorage
  const getReferralCode = (): string | null => {
    // First check URL
    const urlReferralCode = searchParams.get("referralCode");
    if (urlReferralCode) return urlReferralCode;

    // Then check localStorage
    if (typeof window !== "undefined") {
      try {
        const stored = localStorage.getItem(STORAGE_KEYS.referralCode);
        if (stored) {
          const { code, campaign } = JSON.parse(stored);
          if (campaign === CAMPAIGN_CONFIG.id) return code;
        }
      } catch {
        // Invalid stored data
      }
    }
    return null;
  };

  const referralCode = getReferralCode();

  // Store referral code from URL to localStorage
  useEffect(() => {
    const urlReferralCode = searchParams.get("referralCode");
    if (urlReferralCode && typeof window !== "undefined") {
      localStorage.setItem(
        STORAGE_KEYS.referralCode,
        JSON.stringify({ code: urlReferralCode, campaign: CAMPAIGN_CONFIG.id })
      );

      // Track referral code detection
      plausible("Referral: Detected", {
        props: { referralCode: urlReferralCode },
      });
    }
  }, [searchParams, plausible]);

  // Show modal when authenticated and have referral code
  useEffect(() => {
    if (isAuthenticated && address && referralCode) {
      showReferralModal();

      // Track modal opening
      plausible("Referral: Open Modal", {
        props: { referralCode },
      });
    }
  }, [isAuthenticated, address, referralCode, showReferralModal, plausible]);

  const handleActivate = async (code: string) => {
    await activateReferralMutation.mutateAsync(code);
    hideReferralModal();
  };

  const handleClose = () => {
    // Clear stored referral code when closing
    if (typeof window !== "undefined") {
      localStorage.removeItem(STORAGE_KEYS.referralCode);
    }

    // Remove referral from URL
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.delete("referralCode");
      const newUrl = url.pathname + url.search;
      window.history.replaceState({}, "", newUrl);
    }

    // Track modal close
    if (referralCode) {
      plausible("Referral: Close Modal", {
        props: { referralCode },
      });
    }

    hideReferralModal();
  };

  return (
    <ReferralModal
      isOpen={isReferralModalVisible}
      onClose={handleClose}
      referralCode={referralCode}
      onActivate={handleActivate}
      isLoading={activateReferralMutation.isPending}
    />
  );
};